// src/interfaces/formTypes.ts

/**
 * Workflow Forms Type Definitions
 *
 * These types define the schema for dynamic workflow forms that can be
 * triggered via chat. The LLM uses a tool to initiate forms, which are
 * streamed to the frontend as form_request chunks.
 *
 * @see docs/WORKFLOW_FORMS_DESIGN.md for full specification
 */

/**
 * Validation rule for form fields
 */
export interface ValidationRule {
  type: "minLength" | "maxLength" | "pattern" | "min" | "max";
  value: string | number;
  message: string;
}

/**
 * Source of a field's value
 * - document: Extracted from the contract/document content
 * - conversation: Mentioned by the user in chat
 * - inferred: Derived from context but not explicitly stated
 * - default: No value provided, using default/empty
 */
export type FieldSource = "document" | "conversation" | "inferred" | "default";

/**
 * Supported field types for dynamic forms
 */
export type FormFieldType = "text" | "textarea" | "date" | "email" | "number";

/**
 * Individual form field definition
 */
export interface FormField {
  id: string;
  type: FormFieldType;
  label: string;
  placeholder?: string;
  helpText?: string;
  value?: string;
  defaultValue?: string;
  source: FieldSource;
  required?: boolean;
  validation?: ValidationRule[];
  width?: "full" | "half";
}

/**
 * Display configuration for the form
 */
export interface FormDisplay {
  title: string;
  submitButtonText?: string;
}

/**
 * Submission configuration for the form
 */
export interface FormSubmission {
  endpoint: string;
  method: "POST";
}

/**
 * Complete form request payload from the backend
 * This is what arrives in the form_request chunk
 */
export interface FormRequestPayload {
  formType: string;
  formInstanceId: string;
  conversationId?: string;
  display: FormDisplay;
  submission: FormSubmission;
  fields: FormField[];
}

/**
 * Form submission request sent to the backend
 */
export interface FormSubmissionRequest {
  formType: string;
  formInstanceId: string;
  conversationId?: string;
  data: Record<string, unknown>;
  documentUrl?: string | null;
}

/**
 * Successful form submission response
 */
export interface FormSubmissionSuccess {
  success: true;
  message?: string;
}

/**
 * Failed form submission response
 */
export interface FormSubmissionError {
  success: false;
  error: string;
  fieldErrors?: Record<string, string>;
}

/**
 * Union type for form submission responses
 */
export type FormSubmissionResponse = FormSubmissionSuccess | FormSubmissionError;

/**
 * Form lifecycle states
 */
export type FormState = "editable" | "submitting" | "submitted" | "error" | "cancelled";

/**
 * Type guard to check if an object is a valid FormRequestPayload
 */
export function isFormRequestPayload(obj: unknown): obj is FormRequestPayload {
  if (!obj || typeof obj !== "object") {
    return false;
  }

  const payload = obj as Record<string, unknown>;

  return (
    typeof payload.formType === "string" &&
    typeof payload.formInstanceId === "string" &&
    payload.display !== null &&
    typeof payload.display === "object" &&
    typeof (payload.display as Record<string, unknown>).title === "string" &&
    payload.submission !== null &&
    typeof payload.submission === "object" &&
    typeof (payload.submission as Record<string, unknown>).endpoint === "string" &&
    Array.isArray(payload.fields)
  );
}

/**
 * Type guard to check if a response is a successful submission
 */
export function isFormSubmissionSuccess(
  response: FormSubmissionResponse
): response is FormSubmissionSuccess {
  return response.success === true;
}

/**
 * Type guard to check if a response is an error
 */
export function isFormSubmissionError(
  response: FormSubmissionResponse
): response is FormSubmissionError {
  return response.success === false;
}
